import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions, QueryRefetchInterval } from '@cast/shared-types';
import { KentFeature } from '@cast/types-kent';

import { apiClient } from '@cast/infrastructure-api';
import { mapKentFeatureDtoToKentFeature } from 'features/kent/utils';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_CLUSTER_FEATURES_QUERY_KEY = 'kent-cluster-features';

export const getClusterFeaturesQueryKey = (clusterId: string) =>
  [KENT_CLUSTER_FEATURES_QUERY_KEY, clusterId] as const;

const getClusterFeaturesQueryOptions = ({
  organizationId,
  clusterId,
  enabled,
  staleTime,
  refetchInterval,
}: {
  organizationId?: string;
  clusterId: string;
  enabled?: boolean;
  staleTime?: QueryOptions<KentFeature[]>['staleTime'];
  refetchInterval?: QueryRefetchInterval<KentFeature[]>;
}) =>
  queryOptions({
    queryKey: getClusterFeaturesQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getClusterFeatures({
        organizationId: organizationId!,
        clusterId,
      });
      return (data.features || []).map(mapKentFeatureDtoToKentFeature);
    },
    enabled: !!clusterId && enabled,
    staleTime,
    refetchInterval,
  });

type GetClusterFeaturesQueryArgs = QueryOptions<KentFeature[]> & {
  clusterId: string;
};

export const useGetClusterFeaturesQuery = ({
  clusterId,
  enabled,
  staleTime,
  refetchInterval,
}: GetClusterFeaturesQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getClusterFeaturesQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      enabled,
      staleTime,
      refetchInterval,
    })
  );
};
